import { useRef, useState } from "react";
import { ScreenHeader } from "../components/ScreenHeader";
import { useNavigate } from "react-router-dom";
import { toErrorMessage } from "../api/client";
import { Icons } from "../components/Icons";
import { Sheet } from "../components/Sheet";
import { dueDateToIso } from "../lib/date";
import { useMyTasks, type MyTaskItem } from "../hooks/useMyTasks";
import {
  useAddTmComment,
  useCompleteTmTask,
  useCreateTmTask,
  useTmComments,
  useUpdateTmTask,
} from "../hooks/useTmTasks";
import {
  chipMeta,
  LTE_PRIORITY_META,
  LTE_STATUS_META,
  TEMPS_STATUS_META,
  TM_ORIGIN_META,
  TM_ROLE_META,
  TM_STATUS_META,
  type ChipMeta,
} from "../lib/statusMeta";
import { useMyRaciRoles } from "../hooks/useTmAssignments";

/* ── Filtres d'origine (chips de la maquette) ── */
type OriginFilter = "all" | "native" | "terrain" | "temps";
const ORIGIN_FILTERS: { key: OriginFilter; label: string }[] = [
  { key: "all", label: "Toutes" },
  { key: "native", label: "Personnelles" },
  { key: "terrain", label: "Terrain" },
  { key: "temps", label: "Temps" },
];

/* ── Buckets d'échéance (sections datées de la maquette) ── */
type BucketKey = "today" | "week" | "later" | "nodate";
const BUCKET_DEFS: { key: BucketKey; label: string }[] = [
  { key: "today", label: "Aujourd'hui" },
  { key: "week", label: "Cette semaine" },
  { key: "later", label: "Plus tard" },
  { key: "nodate", label: "Sans échéance" },
];

/** Jour calendaire local « YYYY-MM-DD » : comparaison de chaînes suffisante. */
function dayKey(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/** Dernier jour (dimanche) de la semaine locale en cours. */
function endOfWeek(now: Date): Date {
  const end = new Date(now);
  end.setDate(now.getDate() + (6 - ((now.getDay() + 6) % 7)));
  return end;
}

function bucketOf(due: string | undefined, now: Date): BucketKey {
  if (!due) return "nodate";
  const key = dayKey(new Date(due));
  if (key <= dayKey(now)) return "today"; // le retard remonte en tête
  if (key <= dayKey(endOfWeek(now))) return "week";
  return "later";
}

function quickDueIso(key: BucketKey, now: Date): string | undefined {
  if (key === "today") return dueDateToIso(dayKey(now));
  if (key === "week") return dueDateToIso(dayKey(endOfWeek(now)));
  if (key === "later") {
    const d = new Date(now);
    d.setDate(now.getDate() + 14);
    return dueDateToIso(dayKey(d));
  }
  return undefined;
}

function dueLabel(due: string | undefined, now: Date): string {
  if (!due) return "-";
  const d = new Date(due);
  if (dayKey(d) === dayKey(now)) return "Aujourd'hui";
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

function Chip({ meta }: { meta: ChipMeta }) {
  return (
    <span className="chip" style={{ background: meta.bg, color: meta.color }}>
      {meta.label}
    </span>
  );
}

function OriginChip({ origin }: { origin: MyTaskItem["origin"] }) {
  const meta = TM_ORIGIN_META[origin];
  if (!meta) return null;
  return (
    <span className="origin-chip">
      <span className="origin-dot" style={{ background: meta.dot }} />
      {meta.label}
    </span>
  );
}

/* ── Chips de choix (échéance / priorité) des modales ── */
const PRIO_CHIPS: { key: string; label: string; dot: string }[] = [
  { key: "low", label: "Basse", dot: "var(--fg4)" },
  { key: "medium", label: "Normale", dot: "var(--brand)" },
  { key: "high", label: "Haute", dot: "var(--danger)" },
];

function DueChips({
  value,
  onChange,
}: {
  value: BucketKey | "date";
  onChange: (key: BucketKey | "date", isoDate?: string) => void;
}) {
  const [date, setDate] = useState("");
  return (
    <div className="choice-chips">
      {BUCKET_DEFS.map((b) => (
        <button
          key={b.key}
          type="button"
          className={`choice-chip${value === b.key ? " active" : ""}`}
          onClick={() => onChange(b.key)}
        >
          {b.label}
        </button>
      ))}
      <DateChip
        className={`choice-chip${value === "date" ? " active" : ""}`}
        label={
          value === "date" && date
            ? new Date(dueDateToIso(date)).toLocaleDateString("fr-FR")
            : "Date précise…"
        }
        value={date}
        onPick={(iso, raw) => {
          setDate(raw);
          onChange("date", iso);
        }}
      />
    </div>
  );
}

/** Vrai bouton qui ouvre le sélecteur de date natif via showPicker() ;
 * l'input reste caché hors-écran (pas d'overlay fragile). */
function DateChip({
  className,
  label,
  value,
  disabled,
  onPick,
}: {
  className: string;
  label: string;
  value?: string;
  disabled?: boolean;
  onPick: (iso: string, raw: string) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const open = () => {
    const el = ref.current;
    if (!el) return;
    if (el.showPicker) el.showPicker();
    else el.click();
  };
  return (
    <>
      <button
        type="button"
        className={className}
        disabled={disabled}
        onClick={open}
      >
        {label}
      </button>
      <input
        ref={ref}
        type="date"
        value={value}
        aria-label="Date précise"
        tabIndex={-1}
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: "none",
        }}
        onChange={(e) => {
          if (e.target.value)
            onPick(dueDateToIso(e.target.value), e.target.value);
        }}
      />
    </>
  );
}

/** Chip « Date précise… » sans valeur affichée (reprogrammation d'échéance). */
function PickDateChip({
  className,
  disabled,
  onPick,
}: {
  className: string;
  disabled?: boolean;
  onPick: (iso: string) => void;
}) {
  return (
    <DateChip
      className={className}
      label="Date précise…"
      disabled={disabled}
      onPick={(iso) => onPick(iso)}
    />
  );
}

/* ── Modale « Nouvelle tâche personnelle » ──
 * Autonome (aucune donnée de TasksScreen) : aussi montée par HomeScreen, dont le
 * FAB l'ouvre sur place plutôt que de rediriger vers /taches. */
export function NewTaskSheet({
  onClose,
  nested,
}: {
  onClose: () => void;
  nested?: boolean;
}) {
  const create = useCreateTmTask();
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState("medium");
  const [dueKey, setDueKey] = useState<BucketKey | "date">("nodate");
  const [dueIso, setDueIso] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    if (!title.trim() || create.isPending) return;
    create.mutate(
      {
        title: title.trim(),
        priority: priority as "low" | "medium" | "high",
        ...(dueIso ? { due_date: dueIso } : {}),
      },
      {
        // La tâche est créée et le champ reste ouvert pour en enchaîner d'autres.
        onSuccess: () => setTitle(""),
        onError: (e) => setError(toErrorMessage(e)),
      },
    );
  };

  return (
    <Sheet title="Nouvelle tâche personnelle" onClose={onClose} nested={nested}>
      {error && <div className="form-error">{error}</div>}
      <form
        className="inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Intitulé de la tâche…"
          autoFocus
        />
        <label>
          Échéance
          <DueChips
            value={dueKey}
            onChange={(key, iso) => {
              setDueKey(key);
              setDueIso(key === "date" ? iso : quickDueIso(key, new Date()));
            }}
          />
        </label>
        <label>
          Priorité
          <div className="choice-chips">
            {PRIO_CHIPS.map((p) => (
              <button
                key={p.key}
                type="button"
                className={`choice-chip${priority === p.key ? " active" : ""}`}
                onClick={() => setPriority(p.key)}
              >
                <span className="dot" style={{ background: p.dot }} />
                {p.label}
              </button>
            ))}
          </div>
        </label>
        <div className="sheet-cta">
          <button
            type="submit"
            className="btn primary"
            disabled={create.isPending || !title.trim()}
          >
            Créer la tâche
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Fermer
          </button>
        </div>
        <div className="meta-line">
          La tâche est créée et le champ reste ouvert pour en enchaîner
          d'autres.
        </div>
      </form>
    </Sheet>
  );
}

/* ── Modale « Reporter l'échéance » ── */
function RescheduleSheet({
  item,
  onClose,
}: {
  item: MyTaskItem;
  onClose: () => void;
}) {
  const update = useUpdateTmTask();
  const [error, setError] = useState<string | null>(null);

  const pick = (key: BucketKey) => {
    const iso = quickDueIso(key, new Date());
    if (!iso) return;
    update.mutate(
      { id: item.id, body: { due_date: iso } },
      { onSuccess: onClose, onError: (e) => setError(toErrorMessage(e)) },
    );
  };

  return (
    <Sheet title="Reporter l'échéance" onClose={onClose}>
      {error && <div className="form-error">{error}</div>}
      <div className="sheet-cta wrap">
        {(["today", "week", "later"] as const).map((key) => (
          <button
            key={key}
            className="btn"
            disabled={update.isPending}
            onClick={() => pick(key)}
          >
            {BUCKET_DEFS.find((b) => b.key === key)?.label}
          </button>
        ))}
        <PickDateChip
          className="btn"
          disabled={update.isPending}
          onPick={(iso) =>
            update.mutate(
              { id: item.id, body: { due_date: iso } },
              {
                onSuccess: onClose,
                onError: (e) => setError(toErrorMessage(e)),
              },
            )
          }
        />
        <button className="btn ghost" onClick={onClose}>
          Annuler
        </button>
      </div>
    </Sheet>
  );
}

/* ── Modale « Ajouter un commentaire » ── */
function CommentSheet({
  item,
  onClose,
}: {
  item: MyTaskItem;
  onClose: () => void;
}) {
  const addComment = useAddTmComment();
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);

  return (
    <Sheet title="Ajouter un commentaire" onClose={onClose}>
      {error && <div className="form-error">{error}</div>}
      <form
        className="inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!body.trim()) return;
          addComment.mutate(
            { taskId: item.id, body: body.trim() },
            {
              onSuccess: onClose,
              onError: (e2) => setError(toErrorMessage(e2)),
            },
          );
        }}
      >
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Votre commentaire…"
          rows={3}
          autoFocus
        />
        <div className="sheet-cta">
          <button
            type="submit"
            className="btn primary"
            disabled={addComment.isPending || !body.trim()}
          >
            Envoyer
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Annuler
          </button>
        </div>
      </form>
    </Sheet>
  );
}

/* ── Drawer détail tâche personnelle ── */
const TM_STATUS_DEFS = [
  { key: "todo", label: "À faire" },
  { key: "in_progress", label: "En cours" },
  { key: "done", label: "Terminée" },
] as const;

function NativeTaskSheet({
  item,
  onClose,
}: {
  item: MyTaskItem;
  onClose: () => void;
}) {
  const update = useUpdateTmTask();
  const complete = useCompleteTmTask();
  const comments = useTmComments(item.id);
  const addComment = useAddTmComment();
  const raciRole = useMyRaciRoles([item.id])[item.id];
  const [comment, setComment] = useState("");
  const [error, setError] = useState<string | null>(null);

  const setStatus = (status: string) => {
    setError(null);
    if (status === "done") {
      complete.mutate(item.id, {
        onSuccess: onClose,
        onError: (e) => setError(toErrorMessage(e)),
      });
    } else {
      update.mutate(
        { id: item.id, body: { status: status as "todo" | "in_progress" } },
        { onError: (e) => setError(toErrorMessage(e)) },
      );
    }
  };

  return (
    <Sheet title={item.title} onClose={onClose}>
      <div className="row-line">
        <OriginChip origin="native" />
        <Chip meta={chipMeta(TM_STATUS_META, item.status)} />
        <Chip meta={chipMeta(LTE_PRIORITY_META, item.priority ?? "medium")} />
        {raciRole && <Chip meta={chipMeta(TM_ROLE_META, raciRole)} />}
      </div>
      {error && <div className="form-error">{error}</div>}

      <div className="sub-section">
        <div className="section-title" style={{ padding: "0 0 8px" }}>
          Statut
        </div>
        <div className="choice-chips">
          {TM_STATUS_DEFS.map((s) => (
            <button
              key={s.key}
              className={`choice-chip${item.status === s.key ? " active" : ""}`}
              disabled={update.isPending || complete.isPending}
              onClick={() => setStatus(s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="sub-section">
        <div className="section-title" style={{ padding: "0 0 8px" }}>
          Priorité
        </div>
        <div className="choice-chips">
          {PRIO_CHIPS.map((p) => (
            <button
              key={p.key}
              className={`choice-chip${(item.priority ?? "medium") === p.key ? " active" : ""}`}
              disabled={update.isPending}
              onClick={() =>
                update.mutate(
                  {
                    id: item.id,
                    body: { priority: p.key as "low" | "medium" | "high" },
                  },
                  { onError: (e) => setError(toErrorMessage(e)) },
                )
              }
            >
              <span className="dot" style={{ background: p.dot }} />
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="sub-section">
        <div className="section-title" style={{ padding: "0 0 8px" }}>
          Échéance
        </div>
        <div className="meta-line">
          {item.due
            ? new Date(item.due).toLocaleDateString("fr-FR")
            : "Sans échéance"}
        </div>
        <div className="choice-chips">
          {(["today", "week", "later"] as const).map((key) => (
            <button
              key={key}
              className="choice-chip"
              disabled={update.isPending}
              onClick={() => {
                const iso = quickDueIso(key, new Date());
                if (iso)
                  update.mutate(
                    { id: item.id, body: { due_date: iso } },
                    { onError: (e) => setError(toErrorMessage(e)) },
                  );
              }}
            >
              {BUCKET_DEFS.find((b) => b.key === key)?.label}
            </button>
          ))}
          <PickDateChip
            className="choice-chip"
            disabled={update.isPending}
            onPick={(iso) =>
              update.mutate(
                { id: item.id, body: { due_date: iso } },
                { onError: (e) => setError(toErrorMessage(e)) },
              )
            }
          />
        </div>
      </div>

      <div className="sub-section">
        <div className="section-title" style={{ padding: "0 0 8px" }}>
          Commentaires
        </div>
        {(comments.data?.items ?? []).map((c) => (
          <div key={c.id} className="meta-line">
            {c.body}
          </div>
        ))}
        <form
          className="inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!comment.trim()) return;
            addComment.mutate(
              { taskId: item.id, body: comment.trim() },
              {
                onSuccess: () => setComment(""),
                onError: (err) => setError(toErrorMessage(err)),
              },
            );
          }}
        >
          <input
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Ajouter un commentaire…"
          />
        </form>
      </div>
    </Sheet>
  );
}

/* ── Écran ── */
export function TasksScreen() {
  const { items, isLoading, isError } = useMyTasks();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<OriginFilter>("all");
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [rescheduling, setRescheduling] = useState<MyTaskItem | null>(null);
  const [commenting, setCommenting] = useState<MyTaskItem | null>(null);
  const [quickTitle, setQuickTitle] = useState("");
  const create = useCreateTmTask();
  const complete = useCompleteTmTask();

  const raciRoles = useMyRaciRoles(
    items.filter((i) => i.origin === "native").map((i) => i.id),
  );

  // Dérivé des items à chaque render : reflète les mutations (statut, priorité)
  // et se referme seul quand la tâche sort de la liste (terminée).
  const selected =
    items.find((i) => i.origin === "native" && i.id === selectedId) ?? null;

  const now = new Date();
  const tempsItems = items.filter((i) => i.origin === "temps");
  const taskItems = items.filter(
    (i) => i.origin !== "temps" && (filter === "all" || i.origin === filter),
  );
  const buckets: Record<BucketKey, MyTaskItem[]> = {
    today: [],
    week: [],
    later: [],
    nodate: [],
  };
  for (const it of taskItems) buckets[bucketOf(it.due, now)].push(it);

  const showTemps = filter === "all" || filter === "temps";
  const showComposer = filter === "all" || filter === "native";
  const filteredEmpty =
    taskItems.length === 0 && (!showTemps || tempsItems.length === 0);

  const onOpen = (item: MyTaskItem) => {
    // Origines verrouillées (TM_ORIGIN_META.locked) : renvoi vers l'écran source.
    if (item.origin === "terrain") navigate("/terrain");
    else if (item.origin === "temps") navigate("/badger");
    else setSelectedId(item.id);
  };

  const addQuickTask = () => {
    if (!quickTitle.trim() || create.isPending) return;
    create.mutate(
      { title: quickTitle.trim(), priority: "medium" },
      { onSuccess: () => setQuickTitle("") },
    );
  };

  return (
    <div className="team-page tasks-screen">
      <ScreenHeader
        title="Mes tâches"
        sub="Tâches personnelles, terrain et temps réunis en un seul endroit"
      />

      <div
        className="member-chips"
        role="group"
        aria-label="Filtrer par origine"
      >
        {ORIGIN_FILTERS.map((f) => (
          <button
            key={f.key}
            className={`member-chip${filter === f.key ? " active" : ""}`}
            aria-pressed={filter === f.key}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isError && (
        <div className="team-error" role="status">
          <Icons.warn size={17} />
          Connexion perdue, nouvelle tentative en cours…
        </div>
      )}

      {isLoading ? (
        <div style={{ marginTop: 16 }}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="skel-row" />
          ))}
        </div>
      ) : (
        <div className={`team-body${isError ? " dimmed" : ""}`}>
          {items.length === 0 && !isError ? (
            <div className="empty-state">
              <div className="title">Aucune tâche en cours</div>
              <div className="sub">Tout est en ordre ✓</div>
            </div>
          ) : filteredEmpty ? (
            <div className="empty-state">
              <div className="title">Aucune tâche pour ce filtre</div>
              <div className="sub">
                Utilisez le bouton + pour créer une tâche personnelle.
              </div>
            </div>
          ) : (
            <>
              {BUCKET_DEFS.map((b) => {
                const rows = buckets[b.key];
                if (rows.length === 0) return null;
                return (
                  <section key={b.key}>
                    <div className="bucket-head">
                      {b.label}
                      <span className="bucket-count">{rows.length}</span>
                    </div>
                    <div className="mytask-rows">
                      {rows.map((item) => {
                        const locked = item.origin !== "native";
                        const statusMap =
                          item.origin === "terrain"
                            ? LTE_STATUS_META
                            : TM_STATUS_META;
                        return (
                          <div
                            key={`${item.origin}-${item.id}`}
                            className="mytask-row"
                          >
                            {locked ? (
                              <span className="mytask-lock">
                                <Icons.lock size={14} />
                              </span>
                            ) : (
                              <button
                                className="mytask-check"
                                aria-label="Marquer terminée"
                                disabled={complete.isPending}
                                onClick={() => complete.mutate(item.id)}
                              />
                            )}
                            <button
                              className="mytask-main"
                              onClick={() => onOpen(item)}
                            >
                              <span className="mytask-title">{item.title}</span>
                              <span className="row-line">
                                <OriginChip origin={item.origin} />
                                <Chip meta={chipMeta(statusMap, item.status)} />
                                {item.priority && (
                                  <Chip
                                    meta={chipMeta(
                                      LTE_PRIORITY_META,
                                      item.priority,
                                    )}
                                  />
                                )}
                                {item.contextLabel && (
                                  <span className="mytask-meta">
                                    {item.contextLabel}
                                  </span>
                                )}
                                {item.origin === "native" &&
                                  raciRoles[item.id] && (
                                    <Chip
                                      meta={chipMeta(
                                        TM_ROLE_META,
                                        raciRoles[item.id],
                                      )}
                                    />
                                  )}
                                {item.origin === "terrain" && (
                                  <span className="link-terrain">
                                    Ouvrir dans Terrain
                                    <Icons.chev size={12} />
                                  </span>
                                )}
                              </span>
                            </button>
                            <span className="mytask-due">
                              {dueLabel(item.due, now)}
                            </span>
                            {!locked && (
                              <span className="mytask-actions">
                                <button
                                  className="icon-btn"
                                  aria-label="Commenter"
                                  onClick={() => setCommenting(item)}
                                >
                                  <Icons.chat size={15} />
                                </button>
                                <button
                                  className="icon-btn"
                                  aria-label="Reporter l'échéance"
                                  onClick={() => setRescheduling(item)}
                                >
                                  <Icons.cal size={15} />
                                </button>
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </section>
                );
              })}

              {showComposer && (
                <form
                  className="composer"
                  onSubmit={(e) => {
                    e.preventDefault();
                    addQuickTask();
                  }}
                >
                  <Icons.plus size={16} />
                  <input
                    value={quickTitle}
                    onChange={(e) => setQuickTitle(e.target.value)}
                    placeholder="Nouvelle tâche personnelle…  ⏎ pour créer"
                    aria-label="Nouvelle tâche personnelle"
                  />
                </form>
              )}

              {showTemps && tempsItems.length > 0 && (
                <section>
                  <div className="bucket-head">
                    Temps &amp; présence
                    <span className="bucket-count">Lecture seule · TAWA</span>
                  </div>
                  <div className="mytask-rows">
                    {tempsItems.map((item) => (
                      <button
                        key={item.id}
                        className="temps-row mytask-main"
                        style={{ width: "100%" }}
                        onClick={() => navigate("/badger")}
                      >
                        <span className="temps-ic">
                          <Icons.badge size={16} />
                        </span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span className="mytask-title">{item.title}</span>
                        </span>
                        <Chip meta={chipMeta(TEMPS_STATUS_META, item.status)} />
                        {item.contextLabel && (
                          <span className="mytask-due">
                            {item.contextLabel}
                          </span>
                        )}
                        <Icons.chev size={14} className="dim" />
                      </button>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      )}

      <button
        className="fab"
        aria-label="Nouvelle tâche"
        onClick={() => setCreating(true)}
      >
        +
      </button>

      {creating && <NewTaskSheet onClose={() => setCreating(false)} />}
      {selected && (
        <NativeTaskSheet item={selected} onClose={() => setSelectedId(null)} />
      )}
      {rescheduling && (
        <RescheduleSheet
          item={rescheduling}
          onClose={() => setRescheduling(null)}
        />
      )}
      {commenting && (
        <CommentSheet item={commenting} onClose={() => setCommenting(null)} />
      )}
    </div>
  );
}

export default TasksScreen;
